import { DEFAULT_PAGE_SIZE, SortDirection } from '@/components/Table/types';
import { HttpClient } from '@/core/http/http-client';
import {
  ApiResponse,
  DataWithPagination,
  Page,
  PaginationParams,
} from '@/shared/types';
import {
  BusinessAreaEmployee,
  CompanyEmployee,
  CurrentUser,
  OrganizationEmployee,
  TaskUser,
  TenantUser,
  UserBaseInfo,
  UserDetails,
  UserItem,
} from '../domain';
import { AdminPrivilege } from '../domain/admin-privilege';
import { Role } from '../domain/role';
import { UserRateModel } from '../domain/user-rate-model';
import {
  mapBusinessAreEmployeesPage,
  mapCompanyEmployeesPage,
  mapCurrentUser,
  mapOrganizationAdminsPage,
  mapOrganizationEmployeesPage,
  mapTaskUsersPage,
  mapTenantUsersPage,
  mapUserDetails,
  mapUserItemPage,
  mapUsersBaseInfo,
  rolesMapper,
  userRatesWithPaginationMapper,
} from '../mappers';
import { mapAdminPrivilegePage } from '../mappers/admin-privileges';
import {
  ApiResponseRoles,
  ApiResponseUser,
  ApiResponseUserRates,
  ApiResponseUsers,
  BusinessAreaEmployeeJson,
  ChangePasswordJson,
  CompanyEmployeeJson,
  CreateUserJson,
  CurrentUserJson,
  EditUserAccountAdminJson,
  EditUserJson,
  EditUserRoleJson,
  HourlyRateJson,
  LastHourlyRateJson,
  OrganizationAdminJson,
  OrganizationEmployeeJson,
  PagedUserRates,
  TaskUserJson,
  TenantUserJson,
  UserBaseInfoJson,
  UserDetailsJson,
  UserItemJson,
} from '../types';
import {
  AdminPrivilegeJson,
  ApiAdminPrivilegeResponse,
} from '../types/admin-privilege';
import { UserRepository } from './user.repository';

export class HttpUserRepository extends UserRepository {
  constructor(private readonly client: HttpClient) {
    super();
  }

  async getAllUsers(
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<UserItem[]>> {
    const response = await this.client.get<
      ApiResponseUsers<Page<UserItemJson>>
    >({
      url: '/me/users',
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapUserItemPage(response.data.users);
  }

  async getTenantUsers(data: {
    pageNumber?: number;
    filter?: string;
    sortBy?: string;
    sortOrder?: SortDirection;
    isAccountAdmin?: boolean;
  }): Promise<DataWithPagination<TenantUser[]>> {
    const response = await this.client.get<
      ApiResponseUsers<Page<TenantUserJson>>
    >({
      url: '/me/tenants/users',
      query: {
        rowsPerPage: DEFAULT_PAGE_SIZE,
        ...data,
      },
    });

    return mapTenantUsersPage(response.data.users);
  }

  async getAllUsersBaseInfo(query: {
    searchTerm?: string;
    businessAreaId?: number;
  }): Promise<Array<UserBaseInfo>> {
    const response = await this.client.get<
      ApiResponseUsers<UserBaseInfoJson[]>
    >({
      url: 'base-info',
      query,
    });

    return mapUsersBaseInfo(response.data.users);
  }

  async getUserDetails(userId: number): Promise<UserDetails> {
    const response = await this.client.get<ApiResponseUser<UserDetailsJson>>({
      url: `${userId}`,
    });

    return mapUserDetails(response?.data.user);
  }

  async getUserRoles(): Promise<Role[]> {
    const response = await this.client.get<ApiResponseRoles>({
      url: `roles`,
    });

    if (!response) {
      return [];
    }
    return rolesMapper(response?.data.roles);
  }

  async getBusinessAreaUserProfiles(): Promise<Role[]> {
    const response = await this.client.get<ApiResponseRoles>({
      url: `/business-areas/roles`,
    });

    if (!response) {
      return [];
    }

    return rolesMapper(response?.data.roles);
  }

  async enableUser(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `${id}/enable`,
    });
  }

  async disableUser(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `${id}/disable`,
    });
  }

  async addAccess(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `${id}/add-access`,
    });
  }

  async removeAccess(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.post({
      url: `${id}/remove-access`,
    });
  }

  async uploadProfilePicture(data: FormData): Promise<unknown> {
    return await this.client.post<unknown>({
      url: `/me/profile-picture`,
      body: data,
    });
  }

  async deleteProfilePicture(): Promise<unknown> {
    return await this.client.delete<unknown>(`/me/profile-picture`);
  }

  async createUser(
    createUser: CreateUserJson,
  ): Promise<ApiResponse<{ userId: number }>> {
    return (await this.client.post({
      body: createUser,
    })) as ApiResponse<{ userId: number }>;
  }

  async csvUserImport(data: FormData): Promise<void> {
    return await this.client.post({
      body: data,
      url: `csv-import`,
    });
  }

  async getUsersByBusinessArea(
    businessAreaId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<BusinessAreaEmployee[]>> {
    const response = await this.client.get<
      ApiResponseUsers<Page<BusinessAreaEmployeeJson>>
    >({
      url: `/business-areas/${businessAreaId}/users`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapBusinessAreEmployeesPage(response.data.users);
  }

  async getEmployeesByCompany(
    companyId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<CompanyEmployee[]>> {
    const response = await this.client.get<
      ApiResponseUsers<Page<CompanyEmployeeJson>>
    >({
      url: `/companies/${companyId}/employees`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapCompanyEmployeesPage(response.data.users);
  }

  async getEmployeesByOrganization(
    organizationId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<OrganizationEmployee[]>> {
    const response = await this.client.get<
      ApiResponseUsers<Page<OrganizationEmployeeJson>>
    >({
      url: `/organizations/${organizationId}/employees`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapOrganizationEmployeesPage(response.data.users);
  }

  async getAdminsByOrganization(
    organizationId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ) {
    const response = await this.client.get<
      ApiResponseUsers<Page<OrganizationAdminJson>>
    >({
      url: `/organizations/${organizationId}/admins`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapOrganizationAdminsPage(response.data.users);
  }

  async getAdminsByCompany(
    companyId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ) {
    const response = await this.client.get<
      ApiResponseUsers<Page<OrganizationAdminJson>>
    >({
      url: `/companies/${companyId}/admins`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapOrganizationAdminsPage(response.data.users);
  }

  async getUsersByTask(
    taskId: number,
    pageNumber?: number,
    filter?: string,
    sortBy?: string,
    sortOrder?: SortDirection,
  ): Promise<DataWithPagination<TaskUser[]>> {
    const response = await this.client.get<
      ApiResponseUsers<Page<TaskUserJson>>
    >({
      url: `/tasks/${taskId}/users`,
      query: {
        pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter,
        sortBy,
        sortOrder,
      },
    });

    return mapTaskUsersPage(response.data.users);
  }

  async updateUser(editUser: EditUserJson): Promise<ApiResponse<unknown>> {
    return await this.client.put<ApiResponse<unknown>>(
      `${editUser.id}`,
      editUser,
    );
  }

  async updateAccountAdmin(
    editUser: EditUserAccountAdminJson,
  ): Promise<ApiResponse<unknown>> {
    return await this.client.patch<ApiResponse<unknown>>(
      `account-admin`,
      editUser,
    );
  }

  async updateRole(editUser: EditUserRoleJson): Promise<ApiResponse<unknown>> {
    return await this.client.patch<ApiResponse<unknown>>(
      `${editUser.id}/role`,
      editUser,
    );
  }

  async getUserByAccessTokenAndTenantId(
    token: string,
    tenantId: number,
  ): Promise<CurrentUser> {
    const userJson = await this.client.get<ApiResponseUser<CurrentUserJson>>({
      url: `/me`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
          'Tenant-Id': tenantId,
        },
      },
    });

    return mapCurrentUser(userJson?.data.user);
  }

  async getCurrentUser(): Promise<CurrentUser> {
    const response = await this.client.get<ApiResponseUser<CurrentUserJson>>({
      url: `/me`,
    });

    return mapCurrentUser(response?.data.user);
  }

  async getUserRate(
    userId: number,
  ): Promise<DataWithPagination<UserRateModel[]>> {
    const userRateJson = await this.client.get<
      ApiResponseUserRates<PagedUserRates>
    >({
      url: `${userId}/hourly-rates`,
    });
    return userRatesWithPaginationMapper(userRateJson);
  }

  async getAdminPrivilegesByUserId(
    userId: number,
    pagination: PaginationParams,
  ): Promise<DataWithPagination<AdminPrivilege[]>> {
    const response = await this.client.get<
      ApiAdminPrivilegeResponse<Page<AdminPrivilegeJson>>
    >({
      url: `${userId}/admin-privileges`,
      query: {
        pageNumber: pagination.pageNumber,
        rowsPerPage: DEFAULT_PAGE_SIZE,
        filter: pagination.filter,
        sortBy: pagination.sortBy,
        sortOrder: pagination.sortOrder,
      },
    });

    return mapAdminPrivilegePage(response.data.privileges);
  }

  async createUserRate(
    userJson: HourlyRateJson,
  ): Promise<ApiResponse<{ data: { userId: number } }>> {
    return (await this.client.post({
      url: `hourly-rate`,
      body: userJson,
    })) as ApiResponse<{ data: { userId: number } }>;
  }

  async updateUserRate(data: HourlyRateJson): Promise<ApiResponse<unknown>> {
    return await this.client.put<ApiResponse<unknown>>(`hourly-rate`, data);
  }

  async changePassword(
    data: ChangePasswordJson,
  ): Promise<ApiResponse<unknown>> {
    return await this.client.put<ApiResponse<unknown>>(`change-password`, data);
  }

  async deleteUser(id: number): Promise<ApiResponse<unknown>> {
    return await this.client.delete(`${id}`);
  }

  async updateLastAccess(): Promise<void> {
    await this.client.patch<ApiResponse<unknown>>(`/me/last-access`);
  }

  async updateLastHourlyRate(
    lastActiveHourlyRate: LastHourlyRateJson,
  ): Promise<ApiResponse<unknown>> {
    return await this.client.patch<ApiResponse<unknown>>(
      `${lastActiveHourlyRate.id}/last-hourly-rate`,
      lastActiveHourlyRate,
    );
  }
}
